// Отправка голосового — вне жизненного цикла компонентов.
//
// 21.10.2026: Степан записал голосовое, ушёл в другую беседу и через 10 минут
// вернулся — работа не началась. Расшифровку агенту отправлял браузер
// (`deliverInput`) через сокет открытого окна беседы; окно к тому времени
// закрылось, и текст «парковался» в localStorage до следующего открытия беседы.
//
// Теперь «отправить» грузит аудио с `autosend=1`: расшифровку и отправку агенту
// делает сервер, даже если окно уже закрыто. Клиент не ждёт ответа: полоса
// записи закрывается сразу, в беседе висит «расшифровываю…» (своя загрузка —
// здесь, чужая или после перезагрузки — `session.voicePending` с сервера).
// Запрос живёт в этом модуле, а не в компоненте: уход из беседы его не рвёт.
// Черновик в поле ввода не трогается вовсе — текст туда не вставляется.

import { create } from 'zustand'
import { api, ApiError } from '../api'
import type { VoiceResponse } from '../protocol'
import { useLogStore } from '../store/logStore'
import { emptyVoiceReason } from './voiceHints'

interface UploadsState {
  /** sessionId → сколько голосовых этой вкладки ещё грузится/ждёт ответа */
  count: Record<string, number>
}

export const useVoiceUploads = create<UploadsState>(() => ({ count: {} }))

function bump(sessionId: string, delta: number): void {
  useVoiceUploads.setState((s) => {
    const n = Math.max(0, (s.count[sessionId] ?? 0) + delta)
    const count = { ...s.count }
    if (n) count[sessionId] = n
    else delete count[sessionId]
    return { count }
  })
}

/** Клиентская доставка: старый сервер без autosend или режим «текст — мне». */
export type TextSink = (text: string, seconds: number, voiceId?: string | null) => void

function warn(sessionId: string, text: string): void {
  useLogStore.getState().pushLocal(sessionId, { kind: 'warn', gutter: null, text })
}

/**
 * Отдать голосовое серверу на расшифровку с автоотправкой агенту.
 * Ничего не возвращает и ничего не ждёт от вызывающего: ошибки — строкой в лог.
 */
export function sendVoice(
  sessionId: string, blob: Blob, durationSec: number, fallback: TextSink,
): void {
  bump(sessionId, +1)
  void (async () => {
    let res: VoiceResponse
    try {
      res = await api.voice(sessionId, blob, { autosend: true })
    } catch (err) {
      voiceFailed(sessionId, err)
      return
    } finally {
      bump(sessionId, -1)
    }
    const text = (res.text ?? '').trim()
    if (res.sent === true || res.sent === false) {
      // сервер взял доставку на себя; неудачу он уже записал в лог беседы
      return
    }
    // старый сервер не знает autosend — доставляем сами, как раньше
    if (!text) {
      warn(sessionId, `голосовое не отправлено: ${emptyVoiceReason(res, durationSec)}`)
      return
    }
    fallback(text, res.durationSec || durationSec, res.voiceId)
  })()
}

function voiceFailed(sessionId: string, err: unknown): void {
  if (err instanceof ApiError) {
    // 400/500/503 — сбой самой расшифровки: сервер уже написал о нём в лог
    if (err.status === 400 || err.status === 500 || err.status === 503) return
    if (err.status === 408) {
      warn(sessionId, 'сервер долго не отвечает на голосовое — если запись успела загрузиться, ' +
        'расшифровка уйдёт агенту сама (появится строка ♪)')
      return
    }
    warn(sessionId, `голосовое не отправлено: ${err.message}`)
    return
  }
  const msg = err instanceof Error ? err.message : ''
  warn(sessionId, `голосовое не загрузилось на сервер${msg ? `: ${msg}` : ''} — запишите ещё раз`)
}
